import Quill, { Parchment } from 'quill';
import type { Range } from 'quill';
import type { Context } from 'quill/modules/keyboard';
import type Keyboard from 'quill/modules/keyboard';
import { useEffect, useLayoutEffect, useRef } from 'react';

import 'quill/dist/quill.snow.css';
import styles from './quillEditor.module.css';

/**
 * Preserve v1's behavior of carrying the current line's inline formats (bold, size, etc.)
 * onto the new line after Enter. Restore it by re-applying them as cursor formats.
 */
const enterKeepingInlineFormats = {
  key: 'Enter',
  shiftKey: null,
  handler(this: Keyboard, range: Range, context: Context) {
    this.handleEnter(range, context);
    Object.entries(context.format).forEach(([name, value]) => {
      if (Array.isArray(value) || name === 'link' || name === 'code') return;
      if (this.quill.scroll.query(name, Parchment.Scope.BLOCK)) return;
      this.quill.format(name, value, Quill.sources.USER);
    });
  },
};

const MODULES = {
  keyboard: {
    bindings: { enterKeepingInlineFormats },
  },
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    [{ size: ['small', false, 'large'] }],
  ],
};

export interface QuillEditorProps {
  defaultValue?: string;
  onChange?(html: string): void;
  value?: string;
}

/**
 * Modeled off of Quill's [React playground example](https://quilljs.com/playground/react).
 *
 * The onChange handler is modeled after [react-quill's](https://github.com/zenoamaro/react-quill)
 * implementation, which calls the callback with the generated HTML string rather than the Delta objects.
 */
export const QuillEditor = ({
  defaultValue,
  onChange,
  value,
}: QuillEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const defaultValueRef = useRef(defaultValue ?? value ?? '');
  const onChangeRef = useRef(onChange);

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const container = containerRef.current;
    const editorContainer = container?.appendChild(
      container.ownerDocument.createElement('div')
    );
    if (!editorContainer) return;
    const quill = new Quill(editorContainer, {
      modules: MODULES,
      theme: 'snow',
    });

    if (defaultValueRef.current) {
      quill.setContents(
        quill.clipboard.convert({ html: defaultValueRef.current })
      );
    }

    quill.on('text-change', () => {
      onChangeRef.current?.(quill.root.innerHTML);
    });

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  return <div ref={containerRef} className={styles['editor']} />;
};
